import type {ComponentProps, ReactNode} from 'react';
import React from 'react';

import CSSModules from 'react-css-modules';

import Tick from '../../../Course/CompletionScreen/Tick';
import {colourMap} from '../../../icons/utils/iconProperties';
import Text from '../../../TextComponents/Text';

import styles from './styles.module.scss';

type CommonProps = {
  children?: ReactNode;
  scoreText: string;
  heading: string;
  message: string;
  isPreview?: boolean;
  background: string;
  textColour: string;
  ticksRendered?: boolean[];
  numberOfActivitiesText?: string;
  numberOfTicks?: number;
  center?: boolean;
  score?: number;
  tickBoxAltText: string;
  getCompletionTicksAriaLabel: ComponentProps<
    typeof Tick
  >['getCompletionTicksAriaLabel'];
};

type ConditionalEditModeProps =
  | {
      editMode?: false;
      score: number;
    }
  | {
      editMode?: true;
      score?: never;
    };

type ResultsSlideProps = CommonProps & ConditionalEditModeProps;

// eslint-disable-next-line complexity
const ResultsSlide = ({
  score = 100,
  scoreText,
  heading,
  message,
  children: buttons,
  isPreview = false,
  background,
  textColour,
  numberOfActivitiesText,
  ticksRendered,
  numberOfTicks,
  center,
  tickBoxAltText,
  getCompletionTicksAriaLabel,
}: ResultsSlideProps): JSX.Element => {
  let containerStyleName = 'container';

  if (isPreview) {
    containerStyleName += ' preview-mode';
  }
  if (center) {
    containerStyleName += ' center';
  }

  return (
    <div
      styleName={containerStyleName}
      style={{backgroundColor: background || colourMap.brandPrimaryDark}}
      data-automation={`slide-preview${isPreview ? '-thumbnail' : ''}`}
    >
      <div styleName='content'>
        <h2
          styleName='score-heading'
          style={{color: textColour}}
          data-automation={`title-text-preview${isPreview ? '-thumbnail' : ''}`}
        >
          {scoreText}
        </h2>
        <h1
          styleName='score'
          style={{color: textColour}}
          data-automation='assessment-score'
        >
          {score}%
        </h1>
        <h2
          styleName='heading'
          style={{color: textColour}}
          data-automation='assessment-status'
        >
          {heading}
        </h2>
        <>
          <div styleName='tick-container'>
            {!isPreview && (
              <Tick
                ticksRendered={ticksRendered}
                numberOfTicks={numberOfTicks}
                getCompletionTicksAriaLabel={getCompletionTicksAriaLabel}
                tickBoxAltText={tickBoxAltText}
              />
            )}
            <Text
              size='m'
              colour={textColour === '#FFFFFF' ? 'grey050' : 'textPrimary'}
            >
              {numberOfActivitiesText}
            </Text>
          </div>
          <p
            styleName='body'
            data-automation={`assessment-message${
              isPreview ? '-thumbnail' : ''
            }`}
            style={{color: textColour}}
          >
            {message}
          </p>
        </>
        {buttons && !isPreview && buttons}
      </div>
    </div>
  );
};

export default CSSModules(ResultsSlide, styles, {allowMultiple: true});
